import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

/**
 * Zustand AuthStore for managing authentication, farm, and notification state.
 *
 * @module AuthStore
 *
 * @typedef {Object} Credentials
 * @property {string|null} token - JWT access token for the user.
 * @property {string|null} id - User's unique identifier.
 * @property {string|null} firstname - User's first name.
 * @property {string|null} lastname - User's last name.
 * @property {string|null} email - User's email address.
 * @property {string|null} role - User's role (e.g., farmer, supervisor).
 * @property {string|null} status - User's account status.
 * @property {boolean|null} ownFarm - Whether the user owns a farm.
 * @property {string|null} farmName - Name of the user's farm.
 *
 * @typedef {Object} FarmInfo
 * @property {string|null} id - Farm's unique identifier.
 * @property {string|null} farmOwnerId - Owner's user ID.
 * @property {string|null} farmName - Name of the farm.
 * @property {string|null} phoneNumber - Farm's contact number.
 * @property {string|null} farmType - Type of the farm.
 * @property {string|null} country - Country where the farm is located.
 * @property {string|null} state - State where the farm is located.
 * @property {string|null} city - City where the farm is located.
 * @property {string|null} address - Address of the farm.
 * @property {string|null} ownerName - Name of the farm owner.
 * @property {string|null} email - Email of the farm owner.
 * @property {object|null} locations - Location data for the farm.
 * @property {string[]|null} approvedAnimalRegistrations - List of approved animal
 *     registration IDs.
 * @property {string[]|null} rejectedAnimalRegistrations - List of rejected animal
 *     registration IDs.
 * @property {string[]|null} registeredAnimals - List of registered animal IDs.
 * @property {string[]|null} monitoredAnimals - List of monitored animal IDs.
 * @property {string|null} status - Farm status.
 * @property {string|null} createdAt - Farm creation date.
 * @property {string|null} updatedAt - Farm last update date.
 * @property {string|null} lastLogin - Last login date for the farm owner.
 * @property {string|null} assignedSupervisor - Assigned supervisor's ID.
 *
 * @typedef {Object} Notifications
 * @property {number} unread - Number of unread notifications.
 * @property {number} read - Number of read notifications.
 * @property {number} pendingRequests - Number of pending requests.
 * @property {number} processedRequests - Number of processed requests.
 *
 * @typedef {Object} AuthStore
 * @property {Credentials} userInfo - User credentials and info.
 * @property {FarmInfo} farmInfo - Farm information.
 * @property {Notifications} notifications - Notification state.
 * @property {(partialData: Partial<Credentials>) => void} updateCredentials -
 *     Update part of user credentials.
 * @property {(data: Credentials) => void} setCredentials - Set all user
 *     credentials.
 * @property {() => void} logOut - Log out the user and clear credentials.
 * @property {(partialData: Partial<FarmInfo>) => void} updateFarmInfo - Update part
 *     of farm info.
 * @property {(data: FarmInfo) => void} setFarmInfo - Set all farm info.
 * @property {() => void} clearFarmInfo - Clear farm info to initial state.
 * @property {(data: Notifications | ((prev: Notifications) => Notifications)) =>
 *     void} setNotifications - Set notifications state.
 * @property {(partialData: Partial<Notifications>) => void} updateNotifications -
 *     Update part of notifications state.
 *
 * @type {import("zustand").UseBoundStore<AuthStore>}
 */

interface Credentials {
  token: string | null;
  id: string | null;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  role: string | null;
  status: string | null;
  ownFarm: boolean | null;
  farmName: string | null;
}

interface FarmInfo {
  id: string | null;
  farmOwnerId: string | null;
  farmName: string | null;
  phoneNumber: string | null;
  farmType: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  address: string | null;
  ownerName: string | null;
  email: string | null;
  locations: object | null;
  approvedAnimalRegistrations: string[] | null;
  rejectedAnimalRegistrations: string[] | null;
  registeredAnimals: string[] | null;
  monitoredAnimals: string[] | null;
  status: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lastLogin: string | null;
  assignedSupervisor: string | null;
}

interface Notifications {
  unread: number;
  read: number;
  pendingRequests: number;
  processedRequests: number;
}

interface AuthStore {
  userInfo: Credentials;
  farmInfo: FarmInfo;
  notifications: Notifications; // Add notifications to the store
  updateCredentials: (partialData: Partial<Credentials>) => void;
  setCredentials: (data: Credentials) => void;
  logOut: () => void;
  updateFarmInfo: (partialData: Partial<FarmInfo>) => void;
  setFarmInfo: (data: FarmInfo) => void;
  clearFarmInfo: () => void;
  setNotifications: (
    data: Notifications | ((prev: Notifications) => Notifications),
  ) => void; // Updated type
  updateNotifications: (partialData: Partial<Notifications>) => void;
}

const initialState: Credentials = {
  token: null,
  id: null,
  firstname: null,
  lastname: null,
  email: null,
  role: null,
  status: null,
  ownFarm: null,
  farmName: null,
};

const notificationsInitialState: Notifications = {
  unread: 0,
  read: 0,
  pendingRequests: 0,
  processedRequests: 0,
};

const farmInitialState: FarmInfo = {
  id: null,
  farmOwnerId: null,
  farmName: null,
  phoneNumber: null,
  farmType: null,
  country: null,
  state: null,
  city: null,
  address: null,
  ownerName: null,
  email: null,
  locations: null,
  approvedAnimalRegistrations: null,
  rejectedAnimalRegistrations: null,
  registeredAnimals: null,
  monitoredAnimals: null,
  status: null,
  createdAt: null,
  updatedAt: null,
  lastLogin: null,
  assignedSupervisor: null,
};

export const AuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      userInfo: initialState,
      farmInfo: farmInitialState,
      notifications: notificationsInitialState, // Initialize notifications

      setCredentials: (data: Credentials) => {
        set({ userInfo: data });
      },

      updateCredentials: (partialData: Partial<Credentials>) => {
        set((state) => ({ userInfo: { ...state.userInfo, ...partialData } }));
      },

      logOut: () => {
        localStorage.removeItem("auth");
        set({ userInfo: { ...initialState, token: null } });
      },

      setFarmInfo: (data: FarmInfo) => {
        set({ farmInfo: data });
      },

      updateFarmInfo: (partialData: Partial<FarmInfo>) => {
        set((state) => ({ farmInfo: { ...state.farmInfo, ...partialData } }));
      },

      clearFarmInfo: () => {
        set({ farmInfo: farmInitialState });
      },

      setNotifications: (
        data: Notifications | ((prev: Notifications) => Notifications),
      ) => {
        set((state) => ({
          notifications:
            typeof data === "function" ? data(state.notifications) : data,
        }));
      },

      updateNotifications: (partialData: Partial<Notifications>) => {
        set((state) => ({
          notifications: { ...state.notifications, ...partialData }, // Correctly
          // updates
          // notifications
        }));
      },
    }),
    {
      name: "auth",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
